export function topupTotals(cart, config, rate = 1) {
  const goods = cart.items.filter(item => String(item.product_id) !== String(config.protectionId));
  const fees = cart.items.filter(item => String(item.product_id) === String(config.protectionId)).reduce((n, item) => n + item.final_line_price, 0);
  const subtotal = Math.max(0, cart.total_price - fees);
  const threshold = Math.round(config.threshold * (cart.currency === config.baseCurrency ? 1 : rate));
  return { goods, subtotal, threshold, gap: Math.max(0, threshold - subtotal) };
}

export function projectedTopupTotal(cart, config, product, variant, rate = 1) {
  const state = topupTotals(cart, config, rate);
  const eligible = new Set(config.eligibleIds.map(String));
  const quantity = state.goods.reduce((n, item) => n + (eligible.has(String(item.product_id)) ? item.quantity : 0), 0) + (product.eligible ? 1 : 0);
  const percent = quantity >= 4 ? config.discounts[2] : quantity >= 3 ? config.discounts[1] : quantity >= 2 ? config.discounts[0] : 0;
  const factor = Math.max(0, 1 - percent / 100);
  const lineSubtotal = state.goods.reduce((n, item) => n + item.final_line_price, 0);
  const orderFactor = lineSubtotal > 0 ? Math.min(1, state.subtotal / lineSubtotal) : 1;
  const current = state.goods.reduce((n, item) => {
    const discounted = eligible.has(String(item.product_id)) ? Math.ceil(item.original_price * factor) * item.quantity : item.original_line_price;
    return n + Math.min(item.final_line_price, discounted);
  }, 0);
  const added = product.eligible ? Math.ceil(variant.price * factor) : variant.price;
  return Math.floor((current + added) * orderFactor);
}

export function rankTopups(cart, config, products, rate = 1) {
  const state = topupTotals(cart, config, rate);
  if (!state.goods.length || !state.gap || !Number.isFinite(state.threshold) || state.threshold <= 0) return [];
  const added = new Set(cart.items.map(item => String(item.product_id)));
  return products.filter(product => product && !added.has(String(product.id)) && String(product.id) !== String(config.protectionId)).flatMap(product => {
    const variants = product.variants.filter(variant => variant && variant.price > 0).map(variant => ({ variant, projected: projectedTopupTotal(cart, config, product, variant, rate) }));
    variants.sort((a, b) => {
      const aReaches = a.projected >= state.threshold;
      const bReaches = b.projected >= state.threshold;
      return Number(bReaches) - Number(aReaches) || (aReaches ? a.projected - b.projected : b.projected - a.projected) || a.variant.price - b.variant.price;
    });
    return variants.length ? [{ product, ...variants[0] }] : [];
  }).sort((a, b) => {
    const aReaches = a.projected >= state.threshold;
    const bReaches = b.projected >= state.threshold;
    return Number(bReaches) - Number(aReaches) || Math.abs(a.projected - state.threshold) - Math.abs(b.projected - state.threshold) || a.variant.price - b.variant.price;
  }).slice(0, config.limit);
}

if (typeof window !== 'undefined' && !customElements.get('hg-cart-topup')) {
  const views = new Set();
  const mobileDisclosureStates = new Map();
  let connected = false;
  customElements.define('hg-cart-topup', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.data = JSON.parse(this.querySelector('[data-hg-topup-data]').textContent);
      this.data.products = this.data.products.filter(Boolean).map(product => ({ ...product, variants: product.variants.filter(Boolean) }));
      this.cart = this.data.cart;
      this.config = this.data.config;
      this.words = this.data.words;
      this.grid = this.querySelector('[data-hg-topup-grid]');
      this.status = this.querySelector('[data-hg-topup-status]');
      this.disclosure = this.querySelector('[data-hg-topup-disclosure]');
      this.disclosureLabel = this.querySelector('[data-hg-topup-toggle-label]');
      this.mobileMedia = window.matchMedia('(max-width: 767px)');
      this.disclosureKey = this.dataset.hgTopupId;
      const syncDisclosureLabel = () => {
        this.disclosureLabel.textContent = this.disclosure.open ? this.words.hideRecommendations : this.words.viewRecommendations;
      };
      const setDisclosureMode = () => {
        this.disclosure.open = !this.mobileMedia.matches || mobileDisclosureStates.get(this.disclosureKey) === true;
        syncDisclosureLabel();
      };
      this.disclosure.addEventListener('toggle', () => {
        if (this.mobileMedia.matches) mobileDisclosureStates.set(this.disclosureKey, this.disclosure.open);
        syncDisclosureLabel();
      }, { signal: this.controller.signal });
      this.mobileMedia.addEventListener('change', setDisclosureMode, { signal: this.controller.signal });
      setDisclosureMode();
      views.add(this);
      this.addEventListener('submit', event => this.add(event), { signal: this.controller.signal });
      this.paint();
      this.connect = () => {
        if (connected || !window.themeCore?.EventBus) return;
        connected = true;
        window.themeCore.EventBus.listen('cart:updated', snapshot => {
          if (!Array.isArray(snapshot?.items) || typeof snapshot.total_price !== 'number') return;
          for (const view of views) { view.cart = snapshot; view.paint(); }
        });
      };
      document.addEventListener('theme:all:loaded', this.connect, { once: true, signal: this.controller.signal });
      this.connect();
    }
    disconnectedCallback() {
      this.controller?.abort();
      views.delete(this);
      this.controller = null;
    }
    money(amount) {
      const currency = this.cart.currency;
      const zero = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits === 0;
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(zero ? Math.round(amount / 100) : amount / 100);
    }
    element(tag, className, value) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (value !== undefined) node.textContent = value;
      return node;
    }
    paint() {
      this.rate = this.cart.currency === this.config.baseCurrency ? 1 : Number(window.Shopify?.currency?.rate);
      const state = topupTotals(this.cart, this.config, this.rate);
      const ranked = rankTopups(this.cart, this.config, this.data.products, this.rate);
      const editorEmpty = this.config.editorMode && !state.goods.length;
      this.hidden = !ranked.length && !editorEmpty;
      this.disclosure.hidden = !ranked.length;
      this.grid.replaceChildren();
      this.status.textContent = editorEmpty ? this.words.editorHint : '';
      this.querySelector('.hg-topup__note').hidden = Boolean(editorEmpty);
      if (!ranked.length) {
        this.querySelector('[data-hg-topup-gap]').textContent = '';
        return;
      }
      this.querySelector('[data-hg-topup-gap]').textContent = this.words.gap.replace('__AMOUNT__', this.money(state.gap));
      for (const entry of ranked) {
        const { product, variant } = entry;
        const card = this.element('article', 'hg-topup__card');
        const imageLink = this.element('a', 'hg-topup__image');
        imageLink.href = product.url + '?variant=' + variant.id;
        if (product.image) {
          const image = this.element('img');
          image.src = product.image;
          image.alt = product.title;
          image.loading = 'lazy';
          image.width = 144;
          image.height = 144;
          imageLink.append(image);
        } else imageLink.textContent = product.title;
        const content = this.element('div', 'hg-topup__content');
        const title = this.element('a', 'hg-topup__title', product.title);
        title.href = imageLink.href;
        const form = this.element('form', 'hg-topup__form');
        form.method = 'post';
        form.action = this.data.addUrl;
        const select = this.element('select', 'hg-topup__select');
        select.name = 'id';
        select.setAttribute('aria-label', this.words.variant + ': ' + product.title);
        for (const item of product.variants) {
          const option = this.element('option', '', item.title);
          option.value = item.id;
          option.selected = String(item.id) === String(variant.id);
          select.append(option);
        }
        select.hidden = product.variants.length === 1;
        const price = this.element('strong', 'hg-topup__price', this.money(variant.price));
        const estimate = this.element('p', 'hg-topup__estimate');
        const update = () => {
          const selected = product.variants.find(item => String(item.id) === select.value);
          price.textContent = this.money(selected.price);
          estimate.textContent = this.words.estimate.replace('__AMOUNT__', this.money(projectedTopupTotal(this.cart, this.config, product, selected, this.rate)));
          title.href = imageLink.href = product.url + '?variant=' + selected.id;
        };
        select.addEventListener('change', update, { signal: this.controller.signal });
        update();
        const button = this.element('button', 'button button--primary hg-topup__add', this.words.add);
        button.type = 'submit';
        button.setAttribute('aria-label', this.words.add + ': ' + product.title);
        form.append(select, price, estimate, button);
        content.append(title, form);
        card.append(imageLink, content);
        this.grid.append(card);
      }
    }
    async add(event) {
      const form = event.target.closest('.hg-topup__form');
      if (!form) return;
      event.preventDefault();
      if (this.busy || document.body.hasAttribute('data-hg-protection-busy')) return;
      const api = window.themeCore?.CartApi;
      if (!api) { this.status.textContent = this.words.error; return; }
      const id = Number(form.querySelector('[name="id"]').value);
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('button[type="submit"]').forEach(button => { button.disabled = true; });
      this.status.textContent = this.words.adding;
      try {
        const sections = [...document.querySelectorAll('[data-section-type="cart-template"][data-section-id]')].map(node => node.dataset.sectionId).filter(Boolean).slice(0, 5).join(',');
        await api.makeRequest(api.actions.ADD_TO_CART, { id, quantity: 1 }, sections);
        await api.makeRequest(api.actions.GET_CART, { hgBundleSilentRefresh: true, noOpen: true });
        document.dispatchEvent(new CustomEvent('hg:topup:added', { detail: { id } }));
      } catch (error) {
        if (this.isConnected) this.status.textContent = typeof error?.description === 'string' ? error.description : this.words.error;
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        if (this.isConnected) this.querySelectorAll('button[type="submit"]').forEach(button => { button.disabled = false; });
      }
    }
  });
}
